import { useState } from 'react';
import AnimateOnScroll from './AnimateOnScroll';

type Category = 'all' | 'fca' | 'data' | 'switching' | 'cocreator' | 'platform' | 'introducer';

interface FAQ {
  q: string;
  a: string;
  cat: Category;
  icon: string;
}

const faqs: FAQ[] = [
  {
    q: 'How does the Envizage API differ from generic AI tools like ChatGPT?',
    a: "Generic AI tools like ChatGPT, Claude, and Gemini are large language models (LLMs) — they generate plausible-sounding text. They have no financial planning engine underneath and cannot produce auditable, repeatable, forecasts that meet FCA regulations. The Envizage API is a dedicated financial simulation engine built on certified planning logic. Every output is grounded in structured cashflow and actuarial modelling — not statistical text generation. That's why HSBC, Aviva, Barclays, and 18 other global institutions chose it; plausible answers aren't good enough when clients' financial futures are at stake.",
    cat: 'platform',
    icon: 'ri-cpu-line',
  },
  {
    q: 'How do you ensure plans are realistic, not just technically correct?',
    a: "Most planning tools optimise for mathematical accuracy — they'll tell a client they need to save £2,000 per month even if that's impossible given their actual circumstances. The Envizage engine is built with achievability at its core. We model real-world behaviours, life events, and financial constraints to produce plans clients can actually follow. The result is a forecast your client believes in the meeting and acts on afterwards — not one that gets filed and forgotten.",
    cat: 'platform',
    icon: 'ri-scales-3-line',
  },
  {
    q: 'Is Envizage AI regulated?',
    a: 'Envizage operates as a technology provider, not a regulated planner. Our platform generates CFP-grade financial forecasts to support your advisory process — final recommendations are always made by you, the qualified and regulated professional. We are designed to enhance, never replace, your regulatory responsibilities.',
    cat: 'fca',
    icon: 'ri-shield-star-line',
  },
  {
    q: 'How does Envizage AI support Consumer Duty requirements?',
    a: "Our platform is built with Consumer Duty front of mind. The conversational AI ensures clients receive clear, fair, and understandable information about their financial position. Planner-validated outputs mean every plan aligns with your firm's investment propositions, supporting your evidence trail and demonstrating good client outcomes.",
    cat: 'fca',
    icon: 'ri-shield-star-line',
  },
  {
    q: 'Does the AI provide regulated financial advice?',
    a: 'No. Envizage AI provides holistic financial forecasts and planning scenarios — it does not constitute regulated financial advice. All outputs are clearly framed as planning tools for qualified financial planners to inform their professional advice. Responsibility for the final recommendation always rests with the planner.',
    cat: 'fca',
    icon: 'ri-shield-star-line',
  },
  {
    q: "Can the platform support my compliance audit trail?",
    a: 'Every plan generated through Envizage AI creates a full audit trail accessible to your compliance team. Session logs, client inputs, and outputs are stored in a secure, tamper-evident format to support your supervisory and record-keeping obligations under COBS and MiFID II requirements.',
    cat: 'fca',
    icon: 'ri-shield-star-line',
  },
  {
    q: 'How is client data encrypted and protected?',
    a: 'We use AES-256 encryption at rest and TLS 1.3 in transit for all client data. Access controls are strictly role-based and all infrastructure undergoes regular third-party penetration testing. Security incidents are subject to our documented incident response procedure and regulatory notification obligations.',
    cat: 'data',
    icon: 'ri-lock-line',
  },
  {
    q: "Who within Envizage can access my clients' financial data?",
    a: "Nobody.\n\nYour data remains fully accessible for 30 days following cancellation, during which you can export everything via our report facility. After 30 days, all data is securely and permanently deleted.",
    cat: 'data',
    icon: 'ri-lock-line',
  },
  {
    q: 'How quickly can my firm go live?',
    a: "Most firms are live within 48 hours of signing up. Onboarding includes branding setup, investment proposition configuration, and a guided walkthrough with your dedicated account contact. No IT department or complex infrastructure changes are required — it's entirely browser-based.",
    cat: 'switching',
    icon: 'ri-swap-line',
  },
  {
    q: 'Can I migrate data from my existing planning tools?',
    a: 'No, we do not offer migration; however, we offer the ability to upload any form of client data, e.g., a report from a previously used planning tool. Our platform will easily extract the relevant data.',
    cat: 'switching',
    icon: 'ri-swap-line',
  },
  {
    q: 'Does Envizage AI integrate with back-office systems?',
    a: 'We plan to offer integration with back office systems in the priority order determined by our Co-Creators.',
    cat: 'switching',
    icon: 'ri-swap-line',
  },
  {
    q: 'What happens to my data if I cancel my subscription?',
    a: 'Your data remains fully accessible for 30 days following cancellation, during which you can export everything via our report facility. After 30 days, all data is securely and permanently deleted.',
    cat: 'switching',
    icon: 'ri-swap-line',
  },
  {
    q: "What is the Co-Creator founding rate?",
    a: "Co-Creators join at £3.25/month per end user — 75% off the standard £12.99/month per end user rate. This applies throughout 2026, not just for an initial period. The offer closes on 30 September 2026, or sooner if founding spots fill first.",
    cat: 'cocreator',
    icon: 'ri-price-tag-3-line',
  },
  {
    q: "If I have multiple end users as a Co-Creator, do I pay £3.25 × the number of end users?",
    a: "Yes — the Co-Creator rate of £3.25/month applies per end user, the same way the standard rate does. So if you're managing five end users, your total monthly cost as a Co-Creator would be £16.25 (5 × £3.25), compared to £64.95 at the standard rate (5 × £12.99). The 75% saving applies to every end user you add, not just the first one.",
    cat: 'cocreator',
    icon: 'ri-group-line',
  },
  {
    q: 'What happens if I miss the engagement minimum?',
    a: "We run fortnightly feedback sessions — they only last 5–10 minutes each. Miss three and your subscription automatically reverts to the standard monthly rate at the start of the following billing cycle. You'll always be notified before any pricing change takes effect.",
    cat: 'cocreator',
    icon: 'ri-vip-crown-line',
  },
  {
    q: 'How are my Introducer commissions tracked?',
    a: "Every Introducer receives a unique referral URL. Any customer who purchases — either the Single Use option or the Monthly Subscription — through your link is automatically attributed to you. We compile a full report at the end of each calendar month showing how many sales were made via your URL and the total commission due. There is nothing for you to track manually; the report is sent to you directly.",
    cat: 'introducer',
    icon: 'ri-links-line',
  },
  {
    q: 'When will I receive my commission payment?',
    a: "Commission payments are made within 5 business days of the month-end report being issued. So for all sales attributed to your URL during, say, April, you will receive your payment by the 7th or 8th working day of May at the latest. Payment details are confirmed when you register as an Introducer.",
    cat: 'introducer',
    icon: 'ri-money-pound-circle-line',
  },
  {
    q: 'How much do I earn, and is it ongoing?',
    a: "You earn 10% of the billed price for every purchase made through your unique URL — this applies to both the Single Use option (£49.99 one-off, earning you £5.00) and the Monthly Subscription (£12.99/month per end user, earning you £1.30/month per active user).\n\nFor Monthly Subscription customers, your 10% commission repeats every month that the customer remains active. There is no cap and no time limit — as long as they are subscribed, you are earning.\n\nNote: the discounted Co-Creator pricing (£3.25/month) is not commission-eligible.",
    cat: 'introducer',
    icon: 'ri-repeat-line',
  },
  {
    q: 'What happens to my commission if a referred subscriber cancels?',
    a: "If a customer you referred cancels their Monthly Subscription, your recurring commission payments for that customer cease from the following month. You keep everything already paid up to and including the month in which the cancellation takes effect. There are no clawbacks on commissions already received.",
    cat: 'introducer',
    icon: 'ri-calendar-close-line',
  },
  {
    q: "What's on the product roadmap?",
    a: "We're actively building three major features that Co-Creators will get first access to: (1) Read/Understand/Populate — automatically extract and fill client information from uploaded documents like PDFs and photos, saving hours of manual data entry; (2) Top 3 Ways to Achieve What Matters — personalised, AI-ranked action plans showing the most impactful steps toward each client's goals; and (3) Goal Tracking & Monitoring with Notifications — ongoing tracking of client goals with smart alerts when action is needed or meaningful progress is made. As a Co-Creator, your feedback directly shapes the order, design, and functionality of everything we build next.",
    cat: 'cocreator',
    icon: 'ri-rocket-2-line',
  },
];

const categories: { id: Category; label: string; icon: string }[] = [
  { id: 'all', label: 'All Questions', icon: 'ri-list-unordered' },
  { id: 'cocreator', label: 'Co-Creator', icon: 'ri-vip-crown-line' },
  { id: 'introducer', label: 'Introducer', icon: 'ri-share-line' },
  { id: 'platform', label: 'Platform & Accuracy', icon: 'ri-cpu-line' },
  { id: 'fca', label: 'Compliance', icon: 'ri-shield-star-line' },
  { id: 'data', label: 'Data Security', icon: 'ri-lock-line' },
  { id: 'switching', label: 'Switching & Setup', icon: 'ri-swap-line' },
];

function FaqItem({ faq, open, onToggle }: { faq: FAQ; open: boolean; onToggle: () => void }) {
  return (
    <div className={`rounded-2xl border transition-all duration-200 overflow-hidden ${open ? 'border-[#c5ab85] bg-brown-10' : 'border-[#EDE8DF] bg-white hover:border-[#9b7c4e]'}`}>
      <button
        className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`w-6 h-6 flex items-center justify-center rounded flex-shrink-0 mt-0.5 ${open ? 'bg-brown-100' : 'bg-[#F0EBE0]'}`}>
            <i className={`${faq.icon} text-xs ${open ? 'text-white' : 'text-[#1a1a2e]'}`}></i>
          </div>
          <span className="text-md font-semibold text-[#1a1a2e] leading-snug">{faq.q}</span>
        </div>
        <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-transform duration-200 ${open ? 'bg-brown-200 rotate-45' : 'bg-[#F0EBE0]'}`}>
          <i className={`ri-add-line text-sm ${open ? 'text-white' : 'text-[#1a1a2e]'}`}></i>
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-6 pb-5">
          <div className="ml-9 border-l-2 border-[#ffffff] pl-4">
            {faq.a.split('\n\n').map((para, i) => (
              <p key={i} className={`text-md text-[#5a5a6a] leading-relaxed ${i > 0 ? 'mt-3' : ''}`}>{para}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FaqSection() {
  const [activeCat, setActiveCat] = useState<Category>('all');
  const [openIdx, setOpenIdx] = useState<number>(0);

  const filtered = activeCat === 'all' ? faqs : faqs.filter((f) => f.cat === activeCat);
  const countFor = (cat: Category) => cat === 'all' ? faqs.length : faqs.filter((f) => f.cat === cat).length;

  const scrollToRegister = () => {
    const el = document.querySelector('#register');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="faq" className="bg-white py-24 overflow-x-hidden">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          <AnimateOnScroll variant="fade-left" className="lg:col-span-2">
            <div className="lg:sticky lg:top-24 lg:self-start">
              <p className="text-xs font-bold tracking-widest text-brown-300 uppercase mb-4">FAQ</p>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1a1a2e] mb-5 leading-tight">
                Questions Planners Ask Before Signing Up
              </h2>
              <p className="text-md text-[#6a6a7a] leading-relaxed mb-8">
                We know switching planning tools is a big decision. Here are the answers to the questions we hear most — covering compliance, data protection, and getting started.
              </p>
              <div className="flex flex-col gap-2 mb-10">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => { setActiveCat(cat.id); setOpenIdx(-1); }}
                    className={`flex items-center justify-between gap-3 px-4 py-3 rounded-2xl text-md font-medium text-left transition-all cursor-pointer whitespace-nowrap ${
                      activeCat === cat.id ? 'bg-brown-200 text-white' : 'bg-[#EFEBE5] text-[#4a4a4a] hover:bg-[#C5AB85]'
                    }`}
                  >
                    <span className="flex items-center gap-2.5">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-sm`}></i>
                      </div>
                      {cat.label}
                    </span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded whitespace-nowrap ${activeCat === cat.id ? 'bg-white/15 text-white' : 'bg-white text-[#9a9aaa]'}`}>
                      {countFor(cat.id)}
                    </span>
                  </button>
                ))}
              </div>
              {/* <div className="bg-[#EFEBE5] rounded-2xl p-6 border border-[#EDE8DF]">
                <div className="w-10 h-10 flex items-center justify-center bg-brown-100 rounded-xl mb-4">
                  <i className="ri-customer-service-2-line text-white text-lg"></i>
                </div>
                <h4 className="text-md font-bold text-[#1a1a2e] mb-2">Still have questions?</h4>
                <p className="text-xs text-[#6a6a7a] leading-relaxed mb-4">
                  Our team are happy to walk you through anything before you commit.
                </p>
                <button
                  onClick={scrollToRegister}
                  className="w-full bg-brown-500 text-white text-xs font-semibold py-2.5 rounded-full hover:bg-[#000000] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Get in Touch
                </button>
              </div> */}
            </div>
          </AnimateOnScroll>

          <div className="lg:col-span-3 flex flex-col gap-3">
            {filtered.map((faq, i) => (
              <AnimateOnScroll key={faq.q} variant="fade-right" delay={i * 50}>
                <FaqItem
                  faq={faq}
                  open={openIdx === i}
                  onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
                />
              </AnimateOnScroll>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
